import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  FlatList,
  TextInput,
  Dimensions,
  Platform,
  ViewStyle,
  TextStyle,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../theme";

interface DropdownItem {
  id: string | number;
  displayText: string;
  [key: string]: any;
}

interface CrossPlatformDropdownProps {
  childList: DropdownItem[];
  dropdownHeader?: string;
  feedbackType?: "info" | "error" | "success" | "warning";
  helperText?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
  selection?: "single" | "multi";
  setSelectedValue: (value: DropdownItem | DropdownItem[] | null) => void;
  selectedValue: DropdownItem | DropdownItem[] | null;
  size?: "small" | "medium" | "large";
  style?: ViewStyle;
  testID?: string;
  readOnly?: boolean;
  disableSorting?: boolean;
  onSearchTextChange?: (text: string) => void;
  searchPlaceholder?: string;
  labelStyle?: TextStyle;
  placeholderStyle?: TextStyle;
  placement?: "top" | "bottom";
  disabledBackgroundColor?: string;
  showCountOnMultiSelect?: boolean;
  multiSelectCountLabel?: string;
}

const CrossPlatformDropdown: React.FC<CrossPlatformDropdownProps> = ({
  childList = [],
  dropdownHeader = "",
  feedbackType = "info",
  helperText = "",
  isClearable = false,
  isDisabled = false,
  isRequired = false,
  label = "",
  placeholder = "Select...",
  selection = "single",
  setSelectedValue,
  selectedValue = null,
  size = "medium",
  style = {},
  testID,
  readOnly = false,
  disableSorting = false,
  onSearchTextChange,
  searchPlaceholder = "Search...",
  labelStyle = {},
  placeholderStyle = {},
  placement = "bottom",
  disabledBackgroundColor = "#F8F9FA",
  showCountOnMultiSelect = false,
  multiSelectCountLabel = "Selected",
}: CrossPlatformDropdownProps) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const placeholderColor = isDark ? '#8A8A8A' : '#6C757D';
  const arrowColor = isDark ? '#FFFFFF' : '#495057';
  const menuSurfaceColor = isDark ? '#2B2B2B' : '#FFFFFF';
  const menuBorderColor = isDark ? '#404040' : '#E9ECEF';
  const menuTextColor = isDark ? '#FFFFFF' : '#343A40';
  const menuSecondaryTextColor = isDark ? '#9CA3AF' : '#6C757D';
  const selectedItemColor = isDark ? '#1A3A5C' : '#E8F0FE';
  const hoverItemColor = isDark ? '#1C1C1C' : '#E9ECEF';
  const checkIconColor = isDark ? '#60A5FA' : '#0066CC';

  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const dropdownRef = useRef<View>(null);
  const [hoveredItemId, setHoveredItemId] = useState<string | number | null>(
    null
  );
  const [selectedItems, setSelectedItems] = useState<DropdownItem[]>(() => {
    if (!selectedValue) {
      return [];
    }
    return Array.isArray(selectedValue) ? selectedValue : [selectedValue];
  });
  const [_position, setPosition] = useState<"top" | "bottom">("bottom");
  const [dropdownLayout, setDropdownLayout] = useState({
    pageX: 0,
    pageY: 0,
    width: 0,
    height: 300,
  });

  const updateDropdownLayout = useCallback(() => {
    if (!dropdownRef.current) {
      return;
    }
    if (Platform.OS === "web") {
      if (typeof (dropdownRef.current as any).getBoundingClientRect !== 'function') {
        return;
      }
      const rect = (dropdownRef.current as any).getBoundingClientRect();
      const windowHeight =
        Platform.OS === "web" &&
        typeof global !== "undefined" &&
        typeof (global as any).window !== "undefined"
          ? (global as any).window.innerHeight
          : 0;
      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;
      const menuHeight = Math.min(300, windowHeight * 0.6);

      // Determine position based on placement prop
      let newPosition: "top" | "bottom" = placement as "top" | "bottom";
      if (placement === "bottom" && spaceBelow < menuHeight && spaceAbove >= menuHeight) {
        newPosition = "top";
      } else if (placement === "top" && spaceAbove < menuHeight && spaceBelow >= menuHeight) {
        newPosition = "bottom";
      }
      setPosition(newPosition);

      setDropdownLayout({
        pageX: rect.left,
        pageY: newPosition === "bottom" ? rect.bottom : rect.top - menuHeight,
        width: rect.width,
        height: menuHeight,
      });
    } else {
      dropdownRef.current.measureInWindow((x, y, width, height) => {
        const windowHeight = Dimensions.get("window").height;
        const spaceBelow = windowHeight - (y + height);
        const menuHeight = Math.min(300, windowHeight * 0.6);

        const newPosition = spaceBelow >= menuHeight ? "bottom" : "top";
        setPosition(newPosition);

        setDropdownLayout({
          pageX: x,
          pageY: newPosition === "bottom" ? y + height : y - menuHeight,
          width: width,
          height: menuHeight,
        });
      });
    }
  }, [dropdownRef]);

  useEffect(() => {
    if (selectedValue) {
      const items = Array.isArray(selectedValue)
        ? selectedValue
        : [selectedValue];
      setSelectedItems(items);
    } else {
      setSelectedItems([]);
    }
  }, [selectedValue]);
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen && Platform.OS === "web") {
        updateDropdownLayout();
      }
    };

    if (Platform.OS === "web") {
      // Use a safe way to access DOM in React Native Web
      const safeAddEventListener = () => {
        try {
          const doc = (global as any).document;
          if (doc && typeof doc.addEventListener === "function") {
            doc.addEventListener("scroll", handleScroll);
            return () => {
              doc.removeEventListener("scroll", handleScroll);
            };
          }
        } catch (e) {
          // Silent fail if document is not available
        }
      };

      return safeAddEventListener();
    }
  }, [isOpen, updateDropdownLayout]);

  useEffect(() => {
    if (Platform.OS === "web" && isOpen) {
      const handleClick = (e: any) => {
        if (
          e.target.closest(".dropdown-menu") ||
          e.target.closest(".dropdown-search") ||
          e.target.closest("input") ||
          e.target.tagName === "INPUT"
        ) {
          e.stopPropagation();
          return;
        }

        if (!e.target.closest(".modal-content")) {
          setIsOpen(false);
        }
      };

      try {
        const doc = (global as any).document;
        if (doc && typeof doc.addEventListener === "function") {
          doc.addEventListener("click", handleClick, true);
          return () => doc.removeEventListener("click", handleClick, true);
        }
      } catch (e) {
        // Silent fail if document is not available
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchText(""); // Clear search text when dropdown closes
    }
  }, [isOpen]);

  const toggleDropdown = () => {
    if (readOnly) {
      return;
    }
    if (!isOpen) {
      updateDropdownLayout();
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = useCallback((item: DropdownItem) => {
    // Always find the original item from childList to ensure consistent reference
    const originalItem = childList.find(original => original.id === item.id);
    if (!originalItem) {
      console.warn('CrossPlatformDropdown: Selected item not found in original list');
      return;
    }

    if (selection === "single") {
      setSelectedItems([originalItem]);
      
      // Call setSelectedValue immediately to ensure parent state updates synchronously
      setSelectedValue(originalItem);
      
      // Only delay closing the modal to prevent iOS bridge issues
      // but don't delay the value callback
      setTimeout(() => {
        setIsOpen(false);
      }, 50);
    } else {
      const isSelected = selectedItems.some(
        (selected) => selected.id === originalItem.id
      );
      const newSelection = isSelected
        ? selectedItems.filter((selected) => selected.id !== originalItem.id)
        : [...selectedItems, originalItem];
      setSelectedItems(newSelection);
      setSelectedValue(newSelection);
      // Ensure dropdown stays open in multi-select
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  }, [childList, selection, selectedItems, setSelectedValue, isOpen]);

  const clearSelection = (e?: any) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedItems([]);
    setSelectedValue(null);
  };

  // Separate 'All' item and sort the rest alphabetically unless sorting is disabled
  // Memoize to prevent unnecessary recalculations and ensure stable references
  const sortedChildList = useMemo(() => {
    const allItem = childList.find((item) => item.displayText === "All");
    const otherItems = childList.filter((item) => item.displayText !== "All");

    // Only sort if disableSorting is false
    // CRITICAL: Create a copy before sorting to avoid mutating the original array
    const sortedOtherItems = disableSorting
      ? otherItems
      : [...otherItems].sort((a, b) => a.displayText.localeCompare(b.displayText));

    return allItem
      ? [allItem, ...sortedOtherItems]
      : sortedOtherItems;
  }, [childList, disableSorting]);

  const filteredItems = useMemo(() => {
    if (!searchText.trim()) return sortedChildList;

    const search = searchText.toLowerCase().trim();

    return sortedChildList
      // remove duplicates
      .filter(
        (item, i, arr) =>
          i === arr.findIndex((x) => x.displayText === item.displayText)
      )
      // final search filter
      .filter((item) =>
        item.displayText.toLowerCase().trim().includes(search)
      );
  }, [sortedChildList, searchText]);

 

  const getDisplayText = () => {
    if (selectedItems.length === 0) {
      return placeholder;
    }
    if (selection === "multi" && showCountOnMultiSelect && selectedItems.length > 1) {
      return `${selectedItems.length} ${multiSelectCountLabel}`;
    }
    return selection === "single"
      ? selectedItems[0].displayText
      : selectedItems.map((item) => item.displayText).join(", ");
  };

  const renderItem = useCallback(({ item }: { item: DropdownItem }) => {
    const isSelected = selectedItems.some((selected) => selected.id === item.id);
    const isHovered = Platform.OS === "web" && hoveredItemId === item.id;
    
    return (
      <TouchableOpacity
        style={[
          styles.item,
          isDark && { borderBottomColor: '#444444' },
          isSelected && styles.selectedItem,
          isSelected && isDark && { backgroundColor: selectedItemColor },
          isHovered && styles.hoverItem,
          isHovered && isDark && { backgroundColor: hoverItemColor },
        ]}
        onPress={() => handleSelect(item)}
        accessibilityRole="button"
        accessibilityLabel={`${item.displayText}${isSelected ? ", selected" : ""}`}
        {...(Platform.OS === "web"
          ? {
              onMouseEnter: () => setHoveredItemId(item.id),
              onMouseLeave: () => setHoveredItemId(null),
            }
          : {})}
      >
        <Text style={[styles.itemText, { color: menuTextColor }]} numberOfLines={1}>
          {item.displayText}
        </Text>
        {isSelected && (
          <MaterialIcons name="check" size={20} color={checkIconColor} />
        )}
      </TouchableOpacity>
    );
  }, [selectedItems, hoveredItemId, handleSelect, isDark, selectedItemColor, hoverItemColor, menuTextColor, checkIconColor]);

  return (
    <View style={[styles.container, style]} testID={testID ? `${testID}-container` : undefined}>
      {label && (
        <Text style={[styles.label, labelStyle]}>
          {label}
          {isRequired && <Text style={styles.required}> *</Text>}
        </Text>
      )}

      <TouchableOpacity
        ref={dropdownRef}
        testID={testID}
        {...(Platform.OS === "web" ? { className: "dropdown-trigger" } : {})}
        style={[
          styles.dropdownTrigger,
          isDark && { backgroundColor: '#2B2B2B', borderColor: '#404040' },
          size === "small" && styles.smallTrigger,
          size === "large" && styles.largeTrigger,
          (readOnly || isDisabled) && [
            styles.readOnly,
            isDark
              ? { backgroundColor: '#202020', borderColor: '#404040', opacity: 0.8 }
              : { backgroundColor: disabledBackgroundColor },
          ],
        ]}
        onPress={(e) => {
          if (e?.stopPropagation) {
            e.stopPropagation();
          }
          toggleDropdown();
        }}
        disabled={readOnly || isDisabled}
        accessibilityRole="button"
        accessibilityLabel={`${label || "Dropdown"}, ${getDisplayText()}`}
        accessibilityHint="Double tap to open dropdown"
      >
        {/* Display text: apply placeholderStyle when nothing is selected */}
        <Text
          style={[
            styles.selectedText,
            isDark && { color: '#FFFFFF' },
            selectedItems.length === 0 ? [styles.placeholderText, { color: placeholderColor }, placeholderStyle] : undefined,
          ]}
          numberOfLines={1}
        >
          {getDisplayText()}
        </Text>
        <View style={styles.iconContainer}>
          {isClearable && selectedItems.length > 0 && (
            <TouchableOpacity
              style={styles.clearButton}
              onPress={clearSelection}
              hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
              accessibilityRole="button"
              accessibilityLabel="Clear selection"
            >
              <MaterialIcons name="close" size={18} color={arrowColor} />
            </TouchableOpacity>
          )}
          <MaterialIcons
            name={isOpen ? "arrow-drop-up" : "arrow-drop-down"}
            size={24}
            color={arrowColor}
          />
        </View>
      </TouchableOpacity>

      {helperText && (
        <Text
          style={[
            styles.helperText,
            feedbackType === "error" && styles.errorText,
            feedbackType === "success" && styles.successText,
            feedbackType === "warning" && styles.warningText,
          ]}
        >
          {helperText}
        </Text>
      )}

      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setIsOpen(false)}
        supportedOrientations={["portrait", "landscape"]}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={(e) => {
            if (e.target === e.currentTarget) {
              setIsOpen(false);
            }
          }}
        >
          <View
            {...(Platform.OS === "web" ? { className: "modal-content" } : {})}
            style={[
              styles.modalContent,
              { backgroundColor: menuSurfaceColor, borderColor: menuBorderColor, borderWidth: 1 },
              {
                position: "absolute",
                top: dropdownLayout.pageY,
                left: dropdownLayout.pageX,
                width: Math.min(dropdownLayout.width, 300),  // Cap max width at 300px
                maxHeight: dropdownLayout.height,
              },
            ]}
            {...(Platform.OS === "web"
              ? { onClick: (e: any) => e.stopPropagation() }
              : {})}
          >
            <View
              style={[styles.dropdownMenu, { backgroundColor: menuSurfaceColor, borderRadius: 4 }]}
              {...(Platform.OS === "web" ? { className: "dropdown-menu" } : {})}
              {...(Platform.OS === "web"
                ? { onClick: (e: any) => e.stopPropagation() }
                : {})}
            >
              {dropdownHeader && (
                <Text style={[styles.dropdownHeader, { color: menuTextColor, borderBottomColor: menuBorderColor }]}>{dropdownHeader}</Text>
              )}

              <View
                style={[styles.searchContainer, { borderBottomColor: menuBorderColor, backgroundColor: menuSurfaceColor }]}
                {...(Platform.OS === "web"
                  ? { className: "dropdown-search" }
                  : {})}
              >
                <MaterialIcons
                  name="search"
                  size={20}
                  color={menuSecondaryTextColor}
                  style={styles.searchIcon}
                />
                <TextInput
                  style={[
                    styles.searchInput,
                    { color: menuTextColor },
                    isDark
                      ? { backgroundColor: '#202020' }
                      : { backgroundColor: undefined },
                  ]}
                  placeholder={searchPlaceholder}
                  value={searchText}
                  onChangeText={(text) => {
                    // Debounce search in production to prevent performance issues
                    setSearchText(text);
                    
                    // Use requestAnimationFrame for smoother updates in production
                    if (Platform.OS === 'ios') {
                      requestAnimationFrame(() => {
                        if (onSearchTextChange) {
                          onSearchTextChange(text);
                        }
                      });
                    } else {
                      if (onSearchTextChange) {
                        onSearchTextChange(text);
                      }
                    }
                  }}
                  placeholderTextColor={menuSecondaryTextColor}
                  accessibilityLabel="Search dropdown items"
                  {...(Platform.OS === "web"
                    ? {
                        onClick: (e: any) => {
                          e.preventDefault();
                          e.stopPropagation();
                        },
                        onKeyDown: (e: any) => {
                          e.stopPropagation();
                        },
                      }
                    : {})}
                  onFocus={(e: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                />
              </View>

{filteredItems.length > 0 ? (
  <FlatList
                data={filteredItems}
                renderItem={renderItem}
                keyExtractor={(item) => String(item.id)}
                key={`flatlist-${childList.length}-${searchText}`}
                extraData={filteredItems}
                keyboardShouldPersistTaps="always"
                style={styles.list}
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
                // Production optimizations for iOS
                removeClippedSubviews={Platform.OS === 'ios'}
                maxToRenderPerBatch={10}
                windowSize={10}
                initialNumToRender={10}
                getItemLayout={undefined} // Let FlatList handle dynamic sizing
                {...(Platform.OS === "web"
                  ? { onClick: (e: any) => e?.stopPropagation?.() }
                  : {})}
                onScroll={(e) => {
                  if (e?.stopPropagation) {
                    e.stopPropagation();
                  }
                }}
                onTouchStart={(e) => e?.stopPropagation?.()}
                onTouchMove={(e) => e?.stopPropagation?.()}
                onScrollBeginDrag={(e) => e?.stopPropagation?.()}
                ListEmptyComponent={
                  <Text style={[styles.emptyText, { color: menuSecondaryTextColor }]}>No items found</Text>
                }
              />
) : (
<Text style={{ padding: 10, color: menuSecondaryTextColor }}>No items found</Text>
)}
          
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  label: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#343A40",
    marginVertical: 6,
  },
  required: {
    color: "#DC3545",
  },
  dropdownTrigger: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: "#DEE2E6",
    borderRadius: 4,
    backgroundColor: "white",
    minHeight: 40,
  },
  readOnly: {
    opacity: 0.7,
    backgroundColor: "#F8F9FA",
  },
  smallTrigger: {
    minHeight: 32,
    paddingVertical: 6,
  },
  largeTrigger: {
    minHeight: 48,
    paddingVertical: 12,
  },
  selectedText: {
    flex: 1,
    fontSize: 16,
    color: "#343A40",
    marginRight: 8,
  },
  placeholderText: {
    color: "#6C757D",
  },
  iconContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  clearButton: {
    marginRight: 8,
  },
  helperText: {
    fontSize: 12,
    marginTop: 4,
    color: "#0066CC",
  },
  errorText: {
    color: "#DC3545",
  },
  successText: {
    color: "#28A745",
  },
  warningText: {
    color: "#FFC107",
  },
  modalOverlay: Platform.select({
    web: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.3)",
    },
    default: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.3)",
    },
  }) as ViewStyle,
  modalContent: {
    position: "absolute" as const,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#E9ECEF",
    width: 200,
    minWidth: 200,
    maxWidth: 300,
    boxSizing: "border-box" as const,
    ...(Platform.OS === "web" && {
      zIndex: 1000,
      overflow: "visible" as const,
      display: "flex",
      flexDirection: "column",
    }),
  },
  dropdownMenu: {
    backgroundColor: "white",
    borderRadius: 4,
    width: "100%",
    boxSizing: "border-box" as const,
    // maxHeight: 350,
    ...Platform.select({
      ios: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
      },
      android: {
        elevation: 8,
      },
      web: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        display: "flex",
        flexDirection: "column",
      },
    }),
  },
  dropdownHeader: {
    padding: 12,
    fontSize: 16,
    fontWeight: "bold",
    color: "#343A40",
    borderBottomWidth: 1,
    borderBottomColor: "#E9ECEF",
  },
  searchContainer: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#E9ECEF",
    boxSizing: "border-box" as const,
    overflow: "hidden" as const,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    marginLeft: 8,
    fontSize: 16,
    color: "#343A40",
    height: 40,
    padding: 8,
    boxSizing: "border-box" as const,
  },
  list: {
    // height: 250,
    maxHeight: 300,
    ...(Platform.OS === "web"
      ? {
          overflowY: "auto",
          scrollbarWidth: "thin",
        }
      : {
          overflow: "scroll",
        }),
  },
  item: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#E9ECEF",
  },
  selectedItem: {
    backgroundColor: "#E8F0FE",
  },
  hoverItem: {
    backgroundColor: "#E9ECEF",
  },
  itemText: {
    flex: 1,
    fontSize: 16,
    color: "#343A40",
  },
  emptyText: {
    padding: 16,
    textAlign: "center",
    color: "#6C757D",
  },
  searchIcon: {
    flexShrink: 0,
    marginRight: 8,
  } as const,
});

export default CrossPlatformDropdown;
